import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { keyBy } from 'lodash'

import { AnnualTaxFormType } from './annualTaxFormType.slice'
import { fetchIfNeededWrapper, fetchWrapper } from '@/reducers/fetch'
import { adminApiAxios, apiAxios } from '@/api'
import { TAXFYLE_PORTAL_TRY_AGAIN_ERROR } from './constants'

export enum TaxfyleJobStatus {
  underConstruction = 'UNDER_CONSTRUCTION',
  infoGathering = 'INFO_GATHERING',
  unclaimed = 'UNCLAIMED',
  claimed = 'CLAIMED',
  idle = 'IDLE',
  onHold = 'ON_HOLD',
  completed = 'COMPLETED',
  canceled = 'CANCELLED',
}

export enum TaxfyleJobStep {
  jobStarted = 'JOB_STARTED',
  openItems = 'OPEN_ITEMS',
  draftInReview = 'DRAFT_IN_REVIEW',
  draftRejected = 'DRAFT_REJECTED',
  draftApproved = 'DRAFT_APPROVED',
  authorizationRequested = 'AUTHORIZATION_REQUESTED',
  authorizationSigned = 'AUTHORIZATION_SIGNED',
  returnFiled = 'RETURN_FILED',
}

export interface TaxfyleJob {
  jobId: string
  jobStatus: TaxfyleJobStatus
  statusLastUpdatedAt: string | null
  jobStep: TaxfyleJobStep | null
  stepLastUpdatedAt: string | null
  jobStartedAt: string | null
  jobCompletedAt: string | null
  transferredProviderReason: string | null
  cancelledReason: string | null
  cancelledAt: string | null
  taxProLastMessageAt: string | null
  userLastMessageAt: string | null
  hasUnreadMessages: boolean
  messagesLastViewedAt: string | null
  jobType: 'TAX_FILING' | 'EXTENSION' | 'CONSULTATION'
  services: string[]
  createdAt: string
  updatedAt: string
}

export interface TaxfyleCall {
  id: number
  taxfyleJobId: string
  scheduledAt: string
  createdAt: string
  updatedAt: string
  deletedAt: string
}

export interface AnnualTaxFilingForm {
  id: number
  userId: number
  helloSignTemplateId: string | null
  helloSignSignatureRequest: string | null
  helloSignSignedAt: string | null
  annualTaxFilingId: number
  formTypeId: number
  finalFormUserDocumentId?: number
  questionnaireResponseStatus: AnnualTaxFilingFormQuestionnaireStatus
  questionnaireLockedOn: string | null
  zendeskTicketId: number | null
  year: string
  createdAt: string
  updatedAt: string
  formType: Pick<AnnualTaxFormType, 'name' | 'displayName'>
  isExtended: boolean | null
  extensionPayments: AnnualTaxFilingExtensionPayment[] | null
  extensionFiledAt: string | null
  extensionAccepted: boolean | null
  extensionFiledExternally: boolean | null
  completedAt: string | null
  startedAt: string | null
  url: string | null
  taxfyleJob: TaxfyleJob | null
  deletedAt?: string | null
}

export interface AnnualTaxFilingExtensionPayment {
  entityType: 'state' | 'federal'
  entityName: string
  value?: string
  paidAt?: string
}

export interface AnnualTaxFilingFormState {
  [key: string]: AnnualTaxFilingForm
}

const annualTaxFilingFormsSlice = createSlice({
  name: 'annualTaxFilingForms',
  initialState: {} as AnnualTaxFilingFormState,
  reducers: {
    getAllAnnualTaxFilingForms: (
      _state,
      action: PayloadAction<{ [key: string]: AnnualTaxFilingForm }>
    ) => action.payload,
    updateAnnualTaxFilingForm: (
      state,
      action: PayloadAction<AnnualTaxFilingForm>
    ) => {
      state[action.payload.id] = action.payload
    },
    updatePartialAnnualTaxFilingForm: (
      state,
      action: PayloadAction<Partial<AnnualTaxFilingForm> & { id: number }>
    ) => {
      const taxFormEntry = state[action.payload.id]
      if (taxFormEntry) {
        state[action.payload.id] = {
          ...taxFormEntry,
          ...action.payload,
        }
      }
    },
  },
})

export default annualTaxFilingFormsSlice.reducer

// Actions
export const {
  updateAnnualTaxFilingForm,
  getAllAnnualTaxFilingForms,
  updatePartialAnnualTaxFilingForm,
} = annualTaxFilingFormsSlice.actions

export const FETCH_ANNUAL_TAX_FILING_FORMS_KEY =
  'FETCH_ANNUAL_TAX_FILING_FORMS_KEY'
let latestFilingFormsRequestId = 0
export const fetchAnnualTaxFilingFormsIfNeeded = (forceFetch?: boolean) =>
  fetchIfNeededWrapper({
    defaultErrorMessage: 'Error fetching all user tax filings forms',
    fetchKey: FETCH_ANNUAL_TAX_FILING_FORMS_KEY,
    alwaysFetch: forceFetch,
    fetchFunction: async (dispatch) => {
      const requestId = ++latestFilingFormsRequestId
      const json = await apiAxios.get<AnnualTaxFilingForm[]>(
        '/finances/api/v1/annual_tax_filing_forms'
      )

      // The store is replaced wholesale, so an older response landing late would restore removed forms.
      if (requestId === latestFilingFormsRequestId) {
        dispatch(getAllAnnualTaxFilingForms(keyBy(json.data, 'id')))
      }

      return json.data
    },
  })

export enum AnnualTaxFilingFormQuestionnaireStatus {
  'notStarted' = 'not_started',
  'started' = 'started',
  'submitted' = 'submitted',
}

export const updateAdminAnnualTaxFilingForm = (
  annualTaxFilingFormId: number,
  data: Partial<AnnualTaxFilingForm>
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error updating annual tax filing form',
    fetchFunction: async () => {
      const res = await adminApiAxios.put<AnnualTaxFilingForm>(
        `/finances/api/v1/admin/annual_tax_filing_forms/${annualTaxFilingFormId}`,
        data
      )
      return res.data
    },
  })

export const FETCH_TAXFYLE_FORM_JOB_URL_KEY = (
  filingFormId?: number | string
) => `${filingFormId}-FETCH_TAXFYLE_FORM_JOB_URL_KEY`
export const fetchTaxFyleJobUrl = (
  filingFormId?: number | string,
  /** Where the Taxfyle portal's return button sends them. Defaults to the annual tax center. */
  returnOptions?: { url: string; actionLabel: string }
) =>
  fetchWrapper({
    fetchKey: FETCH_TAXFYLE_FORM_JOB_URL_KEY(filingFormId),
    defaultErrorMessage: TAXFYLE_PORTAL_TRY_AGAIN_ERROR,
    fetchFunction: async () => {
      if (!filingFormId) {
        return undefined
      }

      const json = await apiAxios.get<{ jobUrl: string }>(
        `/finances/api/v1/annual_tax_filing_forms/${filingFormId}/job/url`,
        returnOptions
          ? {
              params: {
                return_url: returnOptions.url,
                return_action_label: returnOptions.actionLabel,
              },
            }
          : undefined
      )

      return json.data
    },
  })

export const FETCH_TAXFYLE_EXTENSION_JOB_URL_KEY = (
  filingId?: number | string
) => `${filingId}-FETCH_TAXFYLE_EXTENSION_JOB_URL_KEY`
export const fetchTaxFyleExtensionJobUrl = (filingId?: number | string) =>
  fetchWrapper({
    fetchKey: FETCH_TAXFYLE_EXTENSION_JOB_URL_KEY(filingId),
    defaultErrorMessage: TAXFYLE_PORTAL_TRY_AGAIN_ERROR,
    fetchFunction: async () => {
      if (!filingId) {
        return undefined
      }

      const json = await apiAxios.get<{ jobUrl: string }>(
        `/finances/api/v1/annual_tax_filings/${filingId}/job/url`
      )

      return json.data
    },
  })
